import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Config } from '../../Common/Config';
import { usePatientAuth } from '../context/PatientAuthContext';
import { DUMMY_ACCOUNT } from '../patientDummyData';
import '../PatientAccount.css';
import LDC_LOGO from '../../../Assets/logo.svg';

const USE_DUMMY = false; // ← set false when backend ready

/**
 * IOO-225 – PatientLoginPage (v3)
 * Two-step login:
 *  Step 1 – Email + Password  → validates credentials, then sends OTP to registered mobile
 *  Step 2 – OTP screen        → shows masked mobile (last 4 digits), user enters 6-digit code
 */
export default function PatientLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = usePatientAuth();

  // Auto-redirect if already logged in (persistence check)
  useEffect(() => {
    if (isAuthenticated) {
      const redirect = sessionStorage.getItem('patient_redirect_after_login') || '/dashboard';
      sessionStorage.removeItem('patient_redirect_after_login');
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // ── Step state ──────────────────────────────────────────
  // 'credentials' | 'otp'
  const [step, setStep] = useState('credentials');

  // ── Step 1: Email / Password ────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  // ── Step 2: OTP ─────────────────────────────────────────
  // maskedMobile: e.g. "+44 ****0123"
  const [maskedMobile, setMaskedMobile] = useState('');
  // Stored account from step-1 response (needed to finalise login after OTP)
  const [pendingAccount, setPendingAccount] = useState(null);
  const [pendingToken, setPendingToken] = useState('');

  const OTP_LENGTH = 6;
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef([]);
  const cooldownTimer = useRef(null);

  // ── Forgot Password: Step 1 (request OTP) ───────────────
  const [fpMode, setFpMode] = useState('email'); // 'email' | 'phone'
  const [fpEmail, setFpEmail] = useState('');
  const [fpPhone, setFpPhone] = useState('');
  const [fpCountryCode, setFpCountryCode] = useState('+44');
  const [fpReqLoading, setFpReqLoading] = useState(false);
  const [fpReqError, setFpReqError] = useState('');
  const [fpReqMessage, setFpReqMessage] = useState('');

  // ── Forgot Password: Step 2 (verify OTP) ────────────────
  const [fpOtpDigits, setFpOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [fpOtpLoading, setFpOtpLoading] = useState(false);
  const [fpOtpError, setFpOtpError] = useState('');
  const [fpResendCooldown, setFpResendCooldown] = useState(0);
  const fpOtpRefs = useRef([]);
  const fpCooldownTimer = useRef(null);

  // ── Forgot Password: Step 3 (reset password) ────────────
  const [fpResetToken, setFpResetToken] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpShowNew, setFpShowNew] = useState(false);
  const [fpShowConfirm, setFpShowConfirm] = useState(false);
  const [fpResetLoading, setFpResetLoading] = useState(false);
  const [fpResetError, setFpResetError] = useState('');
  const [fpResetSuccess, setFpResetSuccess] = useState(false);

  // Auto-focus first OTP box when step switches to 'otp'
  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
      startResendCooldown();
    }
    if (step === 'fp-otp') {
      setTimeout(() => fpOtpRefs.current[0]?.focus(), 80);
      startFpResendCooldown();
    }
    return () => { clearInterval(cooldownTimer.current); clearInterval(fpCooldownTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-submit OTP when 6 digits are entered
  useEffect(() => {
    const code = otpDigits.join('');
    if (code.length === OTP_LENGTH && !otpLoading && step === 'otp') {
      handleOtpSubmit(undefined, code);
    }
  }, [otpDigits, otpLoading, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-submit Forgot Password OTP when 6 digits are entered
  useEffect(() => {
    const code = fpOtpDigits.join('');
    if (code.length === OTP_LENGTH && !fpOtpLoading && step === 'fp-otp') {
      handleFpOtpSubmit(undefined, code);
    }
  }, [fpOtpDigits, fpOtpLoading, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus first box when main OTP digits are cleared
  useEffect(() => {
    if (otpDigits.every(d => d === '')) {
      otpRefs.current[0]?.focus();
    }
  }, [otpDigits]);

  // Auto-focus first box when forgot password OTP digits are cleared
  useEffect(() => {
    if (fpOtpDigits.every(d => d === '')) {
      fpOtpRefs.current[0]?.focus();
    }
  }, [fpOtpDigits]);

  // ── Helpers ─────────────────────────────────────────────
  const maskMobile = (countryCode, mobile) => {
    // Show last 4 digits, mask the rest
    const digits = mobile.replace(/\D/g, '');
    const last4 = digits.slice(-4);
    return `${countryCode} ****${last4}`;
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const startFpResendCooldown = () => {
    setFpResendCooldown(60);
    clearInterval(fpCooldownTimer.current);
    fpCooldownTimer.current = setInterval(() => {
      setFpResendCooldown(prev => {
        if (prev <= 1) { clearInterval(fpCooldownTimer.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Step 1: Submit credentials ──────────────────────────
  const validateCreds = () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setPwError('Please enter a valid email address.');
      return false;
    }
    if (password.length < 6) {
      setPwError('Password must be at least 6 characters.');
      return false;
    }
    return true;
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setPwError('');
    if (!validateCreds()) return;
    setPwLoading(true);
    try {
      if (USE_DUMMY) {
        await new Promise(r => setTimeout(r, 900));
        if (email.toLowerCase() === 'wrong@example.com') {
          setPwError('Invalid email or password. Please try again.');
          return;
        }
        // Dummy: credentials OK → store pending account, go to OTP step
        const dummyToken = 'dummy-patient-token-' + Date.now();
        setPendingAccount(DUMMY_ACCOUNT);
        setPendingToken(dummyToken);
        setMaskedMobile(maskMobile(DUMMY_ACCOUNT.country_code, DUMMY_ACCOUNT.mobile_number));
        // Store OTP context in session (for PatientOtpPage compatibility)
        sessionStorage.setItem('patient_otp_mobile', JSON.stringify({
          mobile_number: DUMMY_ACCOUNT.mobile_number,
          country_code: DUMMY_ACCOUNT.country_code,
        }));
        sessionStorage.setItem('patient_otp_message_id', 'dummy-msg-id-' + Date.now());
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setOtpError('');
        setStep('otp');
        return;
      }
      // ── Real API ──
      const res = await fetch(`${Config.urlOpen}patient/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!data.success) {
        setPwError(data.message || 'Invalid email or password.');
        return;
      }
      // Backend returns masked_mobile + requires_2fa; OTP already dispatched server-side
      setMaskedMobile(data.data?.masked_mobile || '');
      // Store email so Step 2 & Resend can reference it
      sessionStorage.setItem('patient_login_email', email);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setOtpError('');
      setStep('otp');
    } catch {
      setPwError('Network error. Please check your connection.');
    } finally {
      setPwLoading(false);
    }
  };

  // ── Step 2: OTP input handlers ───────────────────────────
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    setOtpError('');
    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otpDigits[index]) {
        const next = [...otpDigits];
        next[index] = '';
        setOtpDigits(next);
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setOtpDigits(next);
    setOtpError('');
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[focusIdx]?.focus();
  };

  // ── Step 2: Verify OTP ───────────────────────────────────
  const handleOtpSubmit = async (e, otpValue) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = otpValue !== undefined ? otpValue : otpDigits.join('');
    if (code.length < OTP_LENGTH) {
      setOtpError('Please enter all 6 digits of your verification code.');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      if (USE_DUMMY) {
        await new Promise(r => setTimeout(r, 800));
        // Dummy: any 6-digit code works except 000000
        if (code === '000000') {
          setOtpError('Incorrect verification code. Please try again.');
          setOtpDigits(Array(OTP_LENGTH).fill(''));
          return;
        }
        login(pendingToken, pendingAccount);
        const redirect = sessionStorage.getItem('patient_redirect_after_login') || '/dashboard';
        sessionStorage.removeItem('patient_redirect_after_login');
        navigate(redirect, { replace: true });
        return;
      }
      // ── Real API ──
      const storedEmail = sessionStorage.getItem('patient_login_email') || email;
      const res = await fetch(`${Config.urlOpen}patient/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: storedEmail, otp: code }),
      });
      const data = await res.json();
      if (!data.success) {
        setOtpError(data.message || 'Incorrect code. Please try again.');
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        return;
      }
      const authToken = data.data.token;
      // Fetch full profile with the new token so context has all account fields
      let account = { email: storedEmail, user_id: data.data.user_id };
      try {
        const profileRes = await fetch(`${Config.urlOpen}patient/profile`, {
          headers: { 'Authorization': `Bearer ${authToken}`, 'Accept': 'application/json' },
        });
        const profileData = await profileRes.json();
        if (profileData.success && profileData.data) account = profileData.data;
      } catch { /* fallback to minimal account — profile can be refetched later */ }
      sessionStorage.removeItem('patient_login_email');
      login(authToken, account);
      const redirect = sessionStorage.getItem('patient_redirect_after_login') || '/dashboard';
      sessionStorage.removeItem('patient_redirect_after_login');
      navigate(redirect, { replace: true });
    } catch {
      setOtpError('Network error. Please check your connection.');
      setOtpDigits(Array(OTP_LENGTH).fill(''));
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Step 2: Resend OTP ───────────────────────────────────
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtpError('');
    setOtpDigits(Array(OTP_LENGTH).fill(''));
    startResendCooldown();
    otpRefs.current[0]?.focus();
    if (USE_DUMMY) return; // In dummy mode just reset
    try {
      // Re-calling login re-dispatches the OTP as a server-side side-effect
      const storedEmail = sessionStorage.getItem('patient_login_email') || email;
      await fetch(`${Config.urlOpen}patient/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: storedEmail, password }),
      });
    } catch {
      /* silent */
    }
  };

  // ════════════════════════════════════════════════════════
  //  FORGOT PASSWORD — Step 1: Request OTP
  // ════════════════════════════════════════════════════════
  const handleFpRequest = async (e) => {
    e.preventDefault();
    setFpReqError('');
    if (fpMode === 'email' && !/\S+@\S+\.\S+/.test(fpEmail.trim())) {
      setFpReqError('Please enter a valid email address.'); return;
    }
    if (fpMode === 'phone' && fpPhone.replace(/\D/g, '').length < 7) {
      setFpReqError('Please enter a valid mobile number.'); return;
    }
    setFpReqLoading(true);
    try {
      const payload = fpMode === 'email'
        ? { email: fpEmail.trim() }
        : { country_code: fpCountryCode, mobile_number: fpPhone.trim() };
      const res = await fetch(`${Config.urlOpen}patient/forgot-password-send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) { setFpReqError(data.message || 'Could not send OTP. Please try again.'); return; }
      sessionStorage.setItem('fp_identity', JSON.stringify({ mode: fpMode, email: fpEmail, phone: fpPhone, countryCode: fpCountryCode }));
      setFpReqMessage(data.message || '');
      setFpOtpDigits(Array(OTP_LENGTH).fill(''));
      setFpOtpError('');
      setStep('fp-otp');
    } catch {
      setFpReqError('Network error. Please check your connection.');
    } finally { setFpReqLoading(false); }
  };

  // ════════════════════════════════════════════════════════
  //  FORGOT PASSWORD — Step 2: Verify OTP
  // ════════════════════════════════════════════════════════
  const handleFpOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...fpOtpDigits]; next[index] = digit; setFpOtpDigits(next); setFpOtpError('');
    if (digit && index < OTP_LENGTH - 1) fpOtpRefs.current[index + 1]?.focus();
  };
  const handleFpOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (fpOtpDigits[index]) { const n = [...fpOtpDigits]; n[index] = ''; setFpOtpDigits(n); }
      else if (index > 0) fpOtpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) fpOtpRefs.current[index - 1]?.focus();
    else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) fpOtpRefs.current[index + 1]?.focus();
  };
  const handleFpOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setFpOtpDigits(next); setFpOtpError('');
    fpOtpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };
  const handleFpOtpSubmit = async (e, otpValue) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = otpValue !== undefined ? otpValue : fpOtpDigits.join('');
    if (code.length < OTP_LENGTH) { setFpOtpError('Please enter all 6 digits.'); return; }
    setFpOtpLoading(true); setFpOtpError('');
    try {
      const identity = JSON.parse(sessionStorage.getItem('fp_identity') || '{}');
      const payload = identity.mode === 'email'
        ? { email: identity.email, otp: code }
        : { country_code: identity.countryCode, mobile_number: identity.phone, otp: code };
      const res = await fetch(`${Config.urlOpen}patient/forgot-password-verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        setFpOtpError(data.message || 'Invalid OTP. Please try again.');
        setFpOtpDigits(Array(OTP_LENGTH).fill(''));
        return;
      }
      setFpResetToken(data.data?.reset_token || '');
      setFpNewPassword(''); setFpConfirmPassword(''); setFpResetError(''); setFpResetSuccess(false);
      setStep('fp-reset');
    } catch {
      setFpOtpError('Network error. Please check your connection.');
      setFpOtpDigits(Array(OTP_LENGTH).fill(''));
    } finally { setFpOtpLoading(false); }
  };
  const handleFpResend = async () => {
    if (fpResendCooldown > 0) return;
    setFpOtpError(''); setFpOtpDigits(Array(OTP_LENGTH).fill('')); startFpResendCooldown(); fpOtpRefs.current[0]?.focus();
    try {
      const identity = JSON.parse(sessionStorage.getItem('fp_identity') || '{}');
      const payload = identity.mode === 'email'
        ? { email: identity.email }
        : { country_code: identity.countryCode, mobile_number: identity.phone };
      await fetch(`${Config.urlOpen}patient/forgot-password-send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload),
      });
    } catch { /* silent */ }
  };

  // ════════════════════════════════════════════════════════
  //  FORGOT PASSWORD — Step 3: Reset Password
  // ════════════════════════════════════════════════════════
  const handleFpReset = async (e) => {
    e.preventDefault(); setFpResetError('');
    if (fpNewPassword.length < 8) { setFpResetError('Password must be at least 8 characters.'); return; }
    if (fpNewPassword !== fpConfirmPassword) { setFpResetError('Passwords do not match.'); return; }
    setFpResetLoading(true);
    try {
      const res = await fetch(`${Config.urlOpen}patient/forgot-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ reset_token: fpResetToken, password: fpNewPassword, password_confirmation: fpConfirmPassword }),
      });
      const data = await res.json();
      if (!data.success) { setFpResetError(data.message || 'Could not reset password. Please try again.'); return; }
      sessionStorage.removeItem('fp_identity');
      setFpResetSuccess(true);
    } catch { setFpResetError('Network error. Please check your connection.'); }
    finally { setFpResetLoading(false); }
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="pa-login-outer">
      <div className="pa-login-card pa-login-card-v2">

        {/* Logo */}
        <div className="pa-login-logo-wrap">
          <span className="pa-login-logo-bg">
            <img src={LDC_LOGO} alt="London Doctors Clinic" className="pa-login-logo" style={{ aspectRatio: "282.366 / 37.393" }} />
          </span>
        </div>

        <h1 className="pa-login-title">Patient Account</h1>
        <p className="pa-login-subtitle">Sign in to your account using your password or use "Forgot password" link to set up a new password</p>

        {/* ══════════════ STEP 1: Email + Password ══════════════ */}
        {step === 'credentials' && (
          <form
            onSubmit={handleCredentialsSubmit}
            noValidate
            className="pa-login-form"
            aria-label="Sign in with email and password"
          >
            {/* Step indicator */}
            <div className="pa-step-indicator">
              <span className="pa-step-badge pa-step-badge--active">1</span>
              <span className="pa-step-label">Email &amp; password</span>
              <span className="pa-step-line" />
              <span className="pa-step-badge pa-step-badge--upcoming">2</span>
              <span className="pa-step-label pa-step-label--dim">OTP verification</span>
            </div>

            {/* Email */}
            <div className="pa-form-group">
              <label className="pa-form-label" htmlFor="pa-email-input">Email address</label>
              <div className="pa-input-wrapper">
                <span className="pa-input-icon">✉️</span>
                <input
                  id="pa-email-input"
                  type="email"
                  className="pa-text-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setPwError(''); }}
                  autoComplete="email"
                  required
                  aria-label="Email address"
                />
              </div>
            </div>

            {/* Password */}
            <div className="pa-form-group">
              <label className="pa-form-label" htmlFor="pa-password-input">Password</label>
              <div className="pa-input-wrapper">
                <span className="pa-input-icon"><LockOutlined style={{ color: '#008385' }} /></span>
                <input
                  id="pa-password-input"
                  type={showPassword ? 'text' : 'password'}
                  className="pa-text-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setPwError(''); }}
                  autoComplete="current-password"
                  required
                  aria-label="Password"
                />
                <button
                  type="button"
                  className="pa-pw-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={0}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
            </div>

            {pwError && (
              <div className="pa-field-error pa-field-error-block" role="alert" id="pa-pw-error">
                {pwError}
              </div>
            )}

            <div className="pa-forgot-row">
              <button
                type="button"
                id="pa-forgot-pw-btn"
                className="pa-btn-ghost pa-forgot-link"
                aria-label="Forgot password"
                onClick={() => { setFpEmail(email); setFpReqError(''); setFpReqMessage(''); setStep('fp-request'); }}
              >
                Forgot password?
              </button>
            </div>

            <button
              id="pa-login-pw-btn"
              type="submit"
              className="pa-btn-primary full-width pa-login-submit-btn"
              disabled={pwLoading}
              aria-label="Continue to verification"
            >
              {pwLoading
                ? <><span className="pa-spinner" /> Verifying…</>
                : <><span>Continue</span> <span className="pa-btn-arrow">→</span></>
              }
            </button>

            {USE_DUMMY && (
              <p className="pa-dummy-hint">
                🧪 Dummy mode — any email/password works except <code>wrong@example.com</code>
              </p>
            )}
          </form>
        )}

        {/* ══════════════ STEP 2: OTP Verification ══════════════ */}
        {step === 'otp' && (
          <form
            onSubmit={handleOtpSubmit}
            noValidate
            className="pa-login-form"
            aria-label="Enter verification code"
          >
            {/* Step indicator */}
            <div className="pa-step-indicator">
              <span className="pa-step-badge pa-step-badge--done">✓</span>
              <span className="pa-step-label pa-step-label--dim">Email &amp; password</span>
              <span className="pa-step-line pa-step-line--done" />
              <span className="pa-step-badge pa-step-badge--active">2</span>
              <span className="pa-step-label">OTP verification</span>
            </div>

            {/* Back link */}
            <button
              type="button"
              className="pa-back-link"
              onClick={() => { setStep('credentials'); setOtpError(''); }}
              aria-label="Back to email and password"
            >
              ← Back
            </button>

            {/* Mobile hint */}
            <div className="pa-otp-mobile-hint">
              <span className="pa-otp-phone-icon">📱</span>
              <p>
                We've sent a 6-digit verification code to your registered mobile
                number ending in <strong>{maskedMobile}</strong>.
              </p>
            </div>

            {/* 6-digit OTP boxes */}
            <div className="pa-otp-row" role="group" aria-label="Verification code">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => (otpRefs.current[i] = el)}
                  id={`pa-otp-box-${i}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  className={`pa-otp-box${digit ? ' filled' : ''}${otpError ? ' error' : ''}`}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  aria-label={`Digit ${i + 1} of 6`}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            {otpError && (
              <div className="pa-otp-error" role="alert">
                {otpError}
              </div>
            )}

            <button
              id="pa-verify-otp-btn"
              type="submit"
              className="pa-btn-primary full-width pa-login-submit-btn"
              disabled={otpLoading || otpDigits.join('').length < OTP_LENGTH}
              aria-label="Verify code and sign in"
            >
              {otpLoading
                ? <><span className="pa-spinner" /> Verifying…</>
                : <><span>Verify &amp; Sign In</span> <span className="pa-btn-arrow">→</span></>
              }
            </button>

            {/* Resend */}
            <div className="pa-resend-row">
              Didn't receive it?{' '}
              {resendCooldown > 0
                ? <span className="pa-resend-countdown">Resend in {resendCooldown}s</span>
                : <button
                  type="button"
                  className="pa-resend-link"
                  onClick={handleResend}
                  aria-label="Resend verification code"
                >
                  Resend code
                </button>
              }
            </div>

            {USE_DUMMY && (
              <p className="pa-dummy-hint">
                🧪 Dummy mode — enter any 6 digits except <code>000000</code>
              </p>
            )}
          </form>
        )}

        {/* ════════════ FORGOT PASSWORD — Step 1: Request OTP ════════════ */}
        {step === 'fp-request' && (
          <form onSubmit={handleFpRequest} noValidate className="pa-login-form" aria-label="Forgot password - request OTP">
            <div className="pa-step-indicator">
              <span className="pa-step-badge pa-step-badge--active">1</span>
              <span className="pa-step-label">Identify</span>
              <span className="pa-step-line" />
              <span className="pa-step-badge pa-step-badge--upcoming">2</span>
              <span className="pa-step-label pa-step-label--dim">Verify OTP</span>
              <span className="pa-step-line" />
              <span className="pa-step-badge pa-step-badge--upcoming">3</span>
              <span className="pa-step-label pa-step-label--dim">New password</span>
            </div>
            <button type="button" className="pa-back-link" onClick={() => { setStep('credentials'); setFpReqError(''); }} aria-label="Back to sign in">
              ← Back to Sign In
            </button>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
              Identify yourself with your registered email or mobile phone number and we will send you a one time passcode to your mobile number
            </p>
            {/* Toggle: email / phone */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {['email', 'phone'].map(m => (
                <button key={m} type="button" id={`pa-fp-use-${m}`}
                  onClick={() => { setFpMode(m); setFpReqError(''); }}
                  style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${fpMode === m ? '#008385' : '#ddd'}`, background: fpMode === m ? '#e6f5f5' : '#fff', color: fpMode === m ? '#008385' : '#555', fontWeight: 600, cursor: 'pointer', fontSize: 13, transition: 'all .2s' }}>
                  {m === 'email' ? '✉️ Email' : '📱 Mobile'}
                </button>
              ))}
            </div>
            {fpMode === 'email' ? (
              <div className="pa-form-group">
                <label className="pa-form-label" htmlFor="pa-fp-email">Email address</label>
                <div className="pa-input-wrapper">
                  <span className="pa-input-icon">✉️</span>
                  <input id="pa-fp-email" type="email" className="pa-text-input" placeholder="you@example.com" value={fpEmail}
                    onChange={e => { setFpEmail(e.target.value); setFpReqError(''); }} autoComplete="email" required aria-label="Email for password reset" />
                </div>
              </div>
            ) : (
              <div className="pa-form-group">
                <label className="pa-form-label" htmlFor="pa-fp-phone">Mobile number</label>
                <div className="pa-input-wrapper" style={{ gap: 6 }}>
                  <select id="pa-fp-country-code" value={fpCountryCode} onChange={e => setFpCountryCode(e.target.value)}
                    style={{ border: '1.5px solid #d9d9d9', borderRadius: 8, padding: '0 8px', height: 44, fontSize: 14, color: '#333', background: '#fff', minWidth: 72 }} aria-label="Country code">
                    {['+44', '+1', '+91', '+61', '+971'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input id="pa-fp-phone" type="tel" className="pa-text-input" placeholder="07465208077" value={fpPhone}
                    onChange={e => { setFpPhone(e.target.value); setFpReqError(''); }} autoComplete="tel" required aria-label="Mobile for password reset" />
                </div>
              </div>
            )}
            {fpReqError && <div className="pa-field-error pa-field-error-block" role="alert"> {fpReqError}</div>}
            <button id="pa-fp-send-otp-btn" type="submit" className="pa-btn-primary full-width pa-login-submit-btn" disabled={fpReqLoading} aria-label="Send reset OTP">
              {fpReqLoading ? <><span className="pa-spinner" /> Sending OTP…</> : <><span>Send OTP</span> <span className="pa-btn-arrow">→</span></>}
            </button>
          </form>
        )}

        {/* ════════════ FORGOT PASSWORD — Step 2: Verify OTP ════════════ */}
        {step === 'fp-otp' && (
          <form onSubmit={handleFpOtpSubmit} noValidate className="pa-login-form" aria-label="Forgot password - verify OTP">
            <div className="pa-step-indicator">
              <span className="pa-step-badge pa-step-badge--done">✓</span>
              <span className="pa-step-label pa-step-label--dim">Identify</span>
              <span className="pa-step-line pa-step-line--done" />
              <span className="pa-step-badge pa-step-badge--active">2</span>
              <span className="pa-step-label">Verify OTP</span>
              <span className="pa-step-line" />
              <span className="pa-step-badge pa-step-badge--upcoming">3</span>
              <span className="pa-step-label pa-step-label--dim">New password</span>
            </div>
            <button type="button" className="pa-back-link" onClick={() => { setStep('fp-request'); setFpOtpError(''); }} aria-label="Back">
              ← Back
            </button>
            {fpReqMessage && (
              <div className="pa-otp-mobile-hint">
                <span className="pa-otp-phone-icon">📱</span>
                <p>{fpReqMessage}</p>
              </div>
            )}
            <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>Enter the 6-digit code we sent to reset your password.</p>
            <div className="pa-otp-row" role="group" aria-label="Reset OTP code">
              {fpOtpDigits.map((digit, i) => (
                <input key={i} ref={el => (fpOtpRefs.current[i] = el)}
                  id={`pa-fp-otp-box-${i}`} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={1}
                  className={`pa-otp-box${digit ? ' filled' : ''}${fpOtpError ? ' error' : ''}`}
                  value={digit} onChange={e => handleFpOtpChange(i, e.target.value)}
                  onKeyDown={e => handleFpOtpKeyDown(i, e)} onPaste={i === 0 ? handleFpOtpPaste : undefined}
                  aria-label={`Digit ${i + 1} of 6`} autoComplete="one-time-code" />
              ))}
            </div>
            {fpOtpError && <div className="pa-otp-error" role="alert">{fpOtpError}</div>}
            <button id="pa-fp-verify-otp-btn" type="submit" className="pa-btn-primary full-width pa-login-submit-btn"
              disabled={fpOtpLoading || fpOtpDigits.join('').length < OTP_LENGTH} aria-label="Verify reset OTP">
              {fpOtpLoading ? <><span className="pa-spinner" /> Verifying…</> : <><span>Verify code</span> <span className="pa-btn-arrow">→</span></>}
            </button>
            <div className="pa-resend-row">
              Didn't receive it?{' '}
              {fpResendCooldown > 0
                ? <span className="pa-resend-countdown">Resend in {fpResendCooldown}s</span>
                : <button type="button" className="pa-resend-link" onClick={handleFpResend} aria-label="Resend reset OTP">Resend code</button>
              }
            </div>
          </form>
        )}

        {/* ════════════ FORGOT PASSWORD — Step 3: Reset Password ════════════ */}
        {step === 'fp-reset' && (
          <form onSubmit={handleFpReset} noValidate className="pa-login-form" aria-label="Set new password">
            <div className="pa-step-indicator">
              <span className="pa-step-badge pa-step-badge--done">✓</span>
              <span className="pa-step-label pa-step-label--dim">Identify</span>
              <span className="pa-step-line pa-step-line--done" />
              <span className="pa-step-badge pa-step-badge--done">✓</span>
              <span className="pa-step-label pa-step-label--dim">Verify OTP</span>
              <span className="pa-step-line pa-step-line--done" />
              <span className="pa-step-badge pa-step-badge--active">3</span>
              <span className="pa-step-label">New password</span>
            </div>
            {fpResetSuccess ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <h2 style={{ color: '#008385', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Password Reset!</h2>
                <p style={{ color: '#555', fontSize: 14, marginBottom: 24 }}>Your password has been updated. You can now sign in with your new password.</p>
                <button type="button" id="pa-fp-go-login-btn" className="pa-btn-primary full-width"
                  onClick={() => { setStep('credentials'); setFpResetSuccess(false); setPassword(''); }}>
                  Go to Sign In →
                </button>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>Choose a strong password of at least 8 characters.</p>
                <div className="pa-form-group">
                  <label className="pa-form-label" htmlFor="pa-fp-new-pw">New password</label>
                  <div className="pa-input-wrapper">
                    <span className="pa-input-icon"><LockOutlined style={{ color: '#008385' }} /></span>
                    <input id="pa-fp-new-pw" type={fpShowNew ? 'text' : 'password'} className="pa-text-input" placeholder="At least 8 characters"
                      value={fpNewPassword} onChange={e => { setFpNewPassword(e.target.value); setFpResetError(''); }}
                      autoComplete="new-password" required aria-label="New password" />
                    <button type="button" className="pa-pw-toggle" onClick={() => setFpShowNew(v => !v)}>
                      {fpShowNew ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    </button>
                  </div>
                </div>
                <div className="pa-form-group">
                  <label className="pa-form-label" htmlFor="pa-fp-confirm-pw">Confirm password</label>
                  <div className="pa-input-wrapper">
                    <span className="pa-input-icon"><LockOutlined style={{ color: '#008385' }} /></span>
                    <input id="pa-fp-confirm-pw" type={fpShowConfirm ? 'text' : 'password'} className="pa-text-input" placeholder="Repeat your new password"
                      value={fpConfirmPassword} onChange={e => { setFpConfirmPassword(e.target.value); setFpResetError(''); }}
                      autoComplete="new-password" required aria-label="Confirm new password" />
                    <button type="button" className="pa-pw-toggle" onClick={() => setFpShowConfirm(v => !v)}>
                      {fpShowConfirm ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    </button>
                  </div>
                </div>
                {fpConfirmPassword.length > 0 && (
                  <p style={{ fontSize: 12, color: fpNewPassword === fpConfirmPassword ? '#008385' : '#d9363e', marginTop: -8, marginBottom: 8 }}>
                    {fpNewPassword === fpConfirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
                {fpResetError && <div className="pa-field-error pa-field-error-block" role="alert"> {fpResetError}</div>}
                <button id="pa-fp-reset-btn" type="submit" className="pa-btn-primary full-width pa-login-submit-btn"
                  disabled={fpResetLoading || !fpNewPassword || fpNewPassword !== fpConfirmPassword} aria-label="Set new password">
                  {fpResetLoading ? <><span className="pa-spinner" /> Updating…</> : <><span>Set New Password</span> <span className="pa-btn-arrow">→</span></>}
                </button>
              </>
            )}
          </form>
        )}

        {/* Privacy */}
        <p className="pa-login-privacy">
          By continuing you agree to our{' '}
          <a href="https://www.londondoctorsclinic.co.uk/terms-conditions/privacy-policy/" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>.
        </p>
      </div>
    </div>
  );
}
